import React, { Fragment, useState, useEffect } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Badge, Button, Input, FormGroup, Label, Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import { Breadcrumbs, H4, H5, P } from '../../../../AbstractElements';
import userEventRegApi from '../../../../api/userEventReg';

// Add CSS for hover effects
const hoverStyles = `
  .hover-lift {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  }
  .hover-lift:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
  }
`;

const UserEventRegContain = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Increase default limit

  // Load all user registrations
  useEffect(() => {
    const loadAllRegistrations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const params = {
          page: currentPage,
          limit: pageSize,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          sortBy: 'createdAt',
          sortOrder: 'DESC'
        };
        
        const response = await userEventRegApi.getAllUserEventRegistrations(params);
        // Handle API response format: response.data.registrations
        const registrationsData = response.data?.registrations || response.registrations || response.data || response;
        setRegistrations(Array.isArray(registrationsData) ? registrationsData : []);
        
        // Set statistics if available
        if (response.data?.statistics) {
          setStatistics(response.data.statistics);
        }
        
        // Set pagination if available
        if (response.data?.pagination) {
          setPagination(response.data.pagination);
        }
        
      } catch (err) {
        console.error('❌ Error loading registrations:', err);
        setError('Không thể tải danh sách đăng ký');
        setRegistrations([]);
      } finally {
        setLoading(false);
      }
    };

    loadAllRegistrations();
  }, [currentPage, pageSize, statusFilter]);

  const handleUnregister = async (userId, eventId) => {
    try {
      await userEventRegApi.deleteUserEventRegistration(userId, eventId);
      
      // Reload all registrations
      const response = await userEventRegApi.getAllUserEventRegistrations();
      const registrationsData = response.data?.registrations || response.registrations || response.data || response;
      setRegistrations(Array.isArray(registrationsData) ? registrationsData : []);
      
    } catch (err) {
      console.error('❌ Error unregistering:', err);
      setError('Không thể hủy đăng ký');
    }
  };

  // Filter registrations based on search and status
  const filteredRegistrations = registrations.filter(registration => {
    const matchesSearch = !searchTerm || 
      registration.event?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registration.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registration.event?.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || registration.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'registered':
        return <Badge color="success">Đã đăng ký</Badge>;
      case 'cancelled':
        return <Badge color="secondary">Đã hủy</Badge>;
      case 'attended':
        return <Badge color="info">Đã tham gia</Badge>;
      default:
        return <Badge color="warning">Chưa xác định</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Chưa xác định';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Chưa xác định';
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Đăng ký sự kiện' mainTitle='Đăng ký sự kiện' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải danh sách đăng ký...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  // Show error state
  if (error) {
    return (
      <Fragment>
        <Breadcrumbs parent='Events' title='Đăng ký sự kiện' mainTitle='Đăng ký sự kiện' />
        <Container fluid={true}>
          <div className="py-5">
            <Alert color="danger">
              <strong>Lỗi:</strong> {error}
            </Alert>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <style>{hoverStyles}</style>
      <Breadcrumbs parent='Events' title='Quản lý đăng ký' mainTitle='Quản lý đăng ký sự kiện' />
      <Container fluid={true}>
        <Row>
          <Col>
            <Card className="shadow-sm border-0 mb-4">
              <CardBody className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <H4 className="text-dark mb-0">Tất cả đăng ký sự kiện</H4>
                  <div className="d-flex gap-2">
                    <Badge color="info" className="px-3 py-2">
                      <i className="fa fa-users me-1"></i>
                      {registrations.length} đăng ký
                    </Badge>
                    {statistics && (
                      <Badge color="success" className="px-3 py-2">
                        <i className="fa fa-chart-bar me-1"></i>
                        Tổng: {statistics.totalRegistrations}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Statistics Cards */}
                {statistics && (
                  <Row className="mb-4">
                    <Col md="3">
                      <Card className="text-center border-0 bg-primary text-white">
                        <CardBody className="p-3">
                          <H5 className="mb-1">{statistics.totalRegistrations}</H5>
                          <small>Tổng đăng ký</small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="3">
                      <Card className="text-center border-0 bg-success text-white">
                        <CardBody className="p-3">
                          <H5 className="mb-1">{statistics.statusCounts?.registered || 0}</H5>
                          <small>Đã đăng ký</small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="3">
                      <Card className="text-center border-0 bg-warning text-white">
                        <CardBody className="p-3">
                          <H5 className="mb-1">{statistics.statusCounts?.cancelled || 0}</H5>
                          <small>Đã hủy</small>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col md="3">
                      <Card className="text-center border-0 bg-info text-white">
                        <CardBody className="p-3">
                          <H5 className="mb-1">{statistics.statusCounts?.attended || 0}</H5>
                          <small>Đã tham gia</small>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Search and Filter Controls */}
                <Row className="mb-4">
                  <Col md="4">
                    <FormGroup>
                      <Label for="searchInput" className="fw-medium">Tìm kiếm</Label>
                      <Input
                        type="text"
                        id="searchInput"
                        placeholder="Tìm theo tên sự kiện, user ID, địa điểm..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-0 shadow-sm"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="statusFilter" className="fw-medium">Trạng thái</Label>
                      <Input
                        type="select"
                        id="statusFilter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="border-0 shadow-sm"
                      >
                        <option value="all">Tất cả</option>
                        <option value="registered">Đã đăng ký</option>
                        <option value="cancelled">Đã hủy</option>
                        <option value="attended">Đã tham gia</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="pageSizeSelect" className="fw-medium">Số lượng/trang</Label>
                      <Input
                        type="select"
                        id="pageSizeSelect"
                        value={pageSize}
                        onChange={(e) => setPageSize(parseInt(e.target.value))}
                        className="border-0 shadow-sm"
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {filteredRegistrations.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fa fa-calendar-times text-muted" style={{fontSize: '3rem'}}></i>
                    <p className="text-muted mt-3">
                      {registrations.length === 0 
                        ? 'Chưa có đăng ký sự kiện nào' 
                        : 'Không tìm thấy đăng ký phù hợp'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="row g-4">
                    {filteredRegistrations.map((registration, index) => (
                      <div key={registration.id || index} className="col-md-6 col-lg-4">
                        <Card className="h-100 border-0 shadow-sm hover-lift">
                          <CardBody className="p-3">
                            {/* Event Header with Image */}
                            <div className="mb-3">
                              {registration.event?.img?.[0] && (
                                <div className="mb-3">
                                  <img 
                                    src={registration.event.img[0].mediumUrl || registration.event.img[0].url} 
                                    alt={registration.event.title}
                                    className="img-fluid rounded"
                                    style={{width: '100%', height: '120px', objectFit: 'cover'}}
                                  />
                                </div>
                              )}
                              <div className="d-flex justify-content-between align-items-start">
                                <H5 className="text-dark mb-0">{registration.event?.title || 'Sự kiện'}</H5>
                                {getStatusBadge(registration.status)}
                              </div>
                            </div>
                            
                            {/* User Info */}
                            <div className="mb-3 p-3 bg-light rounded">
                              <div className="d-flex align-items-center">
                                {registration.user?.avatar && (
                                  <img 
                                    src={registration.user.avatar} 
                                    alt="Avatar" 
                                    className="rounded-circle me-3" 
                                    style={{width: '40px', height: '40px', objectFit: 'cover'}}
                                  />
                                )}
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-1">
                                    <i className="fa fa-user text-primary me-2"></i>
                                    <strong className="text-dark">{registration.user?.name || 'Chưa xác định'}</strong>
                                  </div>
                                  <div className="d-flex align-items-center mb-1">
                                    <i className="fa fa-id-card text-secondary me-2"></i>
                                    <small className="text-muted">ID: {registration.userId}</small>
                                  </div>
                                  {registration.user?.pharmacyName && (
                                    <div className="d-flex align-items-center">
                                      <i className="fa fa-store text-success me-2"></i>
                                      <small className="text-muted">{registration.user.pharmacyName}</small>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Event Info */}
                            <div className="mb-3">
                              <div className="d-flex align-items-center mb-2">
                                <i className="fa fa-calendar text-primary me-2"></i>
                                <small className="text-muted">
                                  {formatDate(registration.event?.date)}
                                </small>
                              </div>
                              <div className="d-flex align-items-center mb-2">
                                <i className="fa fa-map-marker text-success me-2"></i>
                                <small className="text-muted">
                                  {registration.event?.location || 'Chưa xác định'}
                                </small>
                              </div>
                              <div className="d-flex align-items-center mb-2">
                                <i className="fa fa-tag text-info me-2"></i>
                                <small className="text-muted">
                                  {registration.event?.type === 'offline' ? 'Trực tiếp' : 'Trực tuyến'}
                                </small>
                              </div>
                              {registration.event?.organizer && (
                                <div className="d-flex align-items-center">
                                  <i className="fa fa-user-tie text-warning me-2"></i>
                                  <small className="text-muted">{registration.event.organizer}</small>
                                </div>
                              )}
                            </div>

                            {registration.notes && (
                              <div className="mb-3">
                                <small className="text-muted d-block">Ghi chú:</small>
                                <P className="text-muted small mb-0">{registration.notes}</P>
                              </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center">
                              <small className="text-muted">
                                Đăng ký: {formatDate(registration.registrationDate)}
                              </small>
                              {registration.status === 'registered' && (
                                <Button 
                                  color="outline-danger" 
                                  size="sm"
                                  onClick={() => handleUnregister(registration.userId, registration.eventId)}
                                >
                                  <i className="fa fa-times me-1"></i>
                                  Hủy đăng ký
                                </Button>
                              )}
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="text-muted">
                      Hiển thị {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} 
                      trong tổng số {pagination.total} đăng ký
                    </div>
                    <Pagination>
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink 
                          previous 
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                      </PaginationItem>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const pageNum = Math.max(1, currentPage - 2) + i;
                        if (pageNum > pagination.totalPages) return null;
                        
                        return (
                          <PaginationItem key={pageNum} active={pageNum === currentPage}>
                            <PaginationLink onClick={() => setCurrentPage(pageNum)}>
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      
                      <PaginationItem disabled={currentPage === pagination.totalPages}>
                        <PaginationLink 
                          next 
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UserEventRegContain;
